import {
  FlaggedMeterDataTable,
  FromToDateToolbar,
  InsightContainer,
  PAGINATION_PAGE_SIZES,
  PipelineInsightsGrid,
  PipelineStatisticsDataTable,
  ResizeableContent,
  RouteBreadcrumbs,
  ThemedPage,
  useApp
} from "@ots-frontend/ots-ui";
import {Card, Container, Grid, Group, Menu, rem, ScrollArea, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {
  adjustToTimeZone,
  flaggedMetersSelector,
  statisticsType,
  useSearchFlaggedMetersQuery
} from "@ots-frontend/ots-data";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {useDispatch} from "react-redux";
import {useGetInsights} from "../../components/hooks/useGetInsights";
import {setPipelineInsightsFilter} from "../../store/pipeline-insights/actions";
import {getPipelineInsightsFilter} from "../../store/pipeline-insights/selectors";

export const PipelineInsightsSearchPage = () => {

  const dispatch = useDispatch()
  const {t} = useTranslation(["translation", "common"]);
  const {regionId, appCategory} = useApp()

  const insightsFilter = useAppSelector(getPipelineInsightsFilter)

  const insights = useGetInsights({
    fromDttm: insightsFilter.fromDttm,
    toDttm: insightsFilter.toDttm,
    regionId: regionId,
    category: appCategory,
    pipeline: true,
    trucking: false,
  })

  const selectFlaggedMeters = useMemo(() => {
    return flaggedMetersSelector();
  }, []);

  const {flaggedMeters, isFlaggedMetersLoading, refetch: refetchFlaggedMeters} = useSearchFlaggedMetersQuery({
    fromDttm: adjustToTimeZone(insightsFilter.fromDttm).getTime(),
    toDttm: adjustToTimeZone(insightsFilter.toDttm).getTime(),
    regionIds: regionId ? [regionId] : undefined,
    categories: appCategory ? [appCategory.code] : undefined,
  }, {
    selectFromResult: (result) => ({
      ...result,
      flaggedMeters: selectFlaggedMeters(result),
      isFlaggedMetersLoading: result.isLoading,
    })
  })

  const {
    statistics: {
      data: statistics,
      isLoading: isStatisticsLoading,
      refetch: refetchStatistics
    }
  } = insights;

  const regionStats = useMemo(() => {
    return regionId ? statistics?.getRegionStats(regionId) : undefined
  }, [regionId, statistics])

  const pipelineStats = useMemo(() => {
    return statistics?.pipelineStats ?? []
  }, [statistics])

  const onRefreshClick = useCallback(() => {
    if (refetchStatistics) {
      refetchStatistics()
    }
    if (refetchFlaggedMeters) {
      refetchFlaggedMeters()
    }
  }, [refetchStatistics])

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {
    dispatch(setPipelineInsightsFilter({
      fromDttm: fromDate.getTime(),
      toDttm: toDate?.getTime()
    }));
  }, [dispatch]);

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <ScrollArea h={size.height} p="sm" offsetScrollbars={true}>
                <RouteBreadcrumbs p="sm"/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.site.pipelineinsightssearch.title", "Historical Pipeline Insights", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("commons.action.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => onRefreshClick()}>
                          {t("commons.action.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <FromToDateToolbar
                    defaultValues={{
                      fromDate: new Date(insightsFilter.fromDttm),
                      toDate: insightsFilter.toDttm ? new Date(insightsFilter.toDttm) : undefined,
                    }}
                    loading={isStatisticsLoading}
                    onSubmit={onFilterChange}/>
                </Card>

                <PipelineInsightsGrid
                  insights={regionStats}
                  rateModels={['Pipeline']}
                  category={appCategory}
                  loading={isStatisticsLoading}>

                  <Grid.Col span={12}>
                    <InsightContainer title={t<string>("common.insight.bypipeline", "By Pipeline")}>
                      <PipelineStatisticsDataTable
                        m="md"
                        withBorder
                        records={pipelineStats}
                        recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                        fetching={isStatisticsLoading}
                        types={[statisticsType.quantity, statisticsType.cost]}
                        category={appCategory}
                        regionId={regionId}
                        fromDttm={insightsFilter.fromDttm}
                        toDttm={insightsFilter.toDttm}/>
                    </InsightContainer>
                  </Grid.Col>

                  {flaggedMeters.length > 0 &&
                    <Grid.Col span={12}>
                      <InsightContainer
                        title={t<string>("common.insight.flaggedmeters", "Flagged Meters")}
                        icon={"flag"}>
                        <FlaggedMeterDataTable
                          m="md"
                          withBorder={true}
                          withColumnBorders={true}
                          records={flaggedMeters}
                          recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                          fetching={isFlaggedMetersLoading}/>
                      </InsightContainer>
                    </Grid.Col>
                  }

                </PipelineInsightsGrid>
              </ScrollArea>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
